import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircleIcon, ExclamationTriangleIcon, LockClosedIcon, ChevronDownIcon, XCircleIcon } from '@heroicons/react/24/outline';
import quoteService from '../../services/quoteService';
import Logo from '../../components/Logo';

const REFUSED = {
  invalid: ['This link isn’t valid', 'Please check that you opened the complete link from your email or message.'],
  expired: ['This estimate has expired', 'Please contact the practice for an up-to-date estimate.'],
  cancelled: ['This estimate is no longer active', 'Please contact the practice.'],
  superseded: ['There is a newer estimate', 'The practice has sent you an updated estimate — please use the newest link.'],
  no_practice: ['This page isn’t linked to a practice', 'Please open the link exactly as you received it.'],
};

const rands = (n) => (n == null ? '—' : `R ${Number(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}`);
const fmtDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const fmtWhen = (d) => new Date(d).toLocaleString('en-ZA', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });

/**
 * PublicQuote — the page a patient opens from the practice's link: an estimate
 * of the anaesthetic fees for their procedure, what their medical aid is likely
 * to pay and what they may need to pay, and a place to accept it (informed
 * financial consent) or decline it. No login; the link works once.
 */
const PublicQuote = () => {
  const { token } = useParams();
  const [phase, setPhase] = useState('loading'); // loading | view | refused | done
  const [data, setData] = useState(null);
  const [refused, setRefused] = useState(null);
  const [name, setName] = useState('');
  const [agree, setAgree] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [showLines, setShowLines] = useState(false);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [decision, setDecision] = useState(null);

  useEffect(() => {
    quoteService.publicLoad(token)
      .then((d) => { setData(d); setPhase('view'); })
      .catch((e) => {
        const d = e.response?.data;
        setRefused({ ...d, code: d?.code || 'invalid', network: !e.response });
        setPhase('refused');
      });
  }, [token]);

  const respond = async (choice) => {
    setSaving(choice);
    setError('');
    try {
      await quoteService.publicRespond(token, { decision: choice, full_name: name, agree, note: choice === 'decline' ? reason : undefined });
      setDecision(choice);
      setPhase('done');
      window.scrollTo({ top: 0 });
    } catch (e) {
      const d = e.response?.data;
      if (d?.code && (REFUSED[d.code] || d.code === 'answered')) {
        setRefused(d);
        setPhase('refused');
      } else {
        setError(d?.err || (e.response ? 'Something went wrong — please try again.' : 'You seem to be offline — check your connection and try again.'));
      }
    } finally {
      setSaving('');
    }
  };

  const practice = data?.practice || refused?.practice;
  const q = data?.quote;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-4 py-3">
          <div className="min-w-0">
            <div className="truncate font-bold text-brand-deep">{practice?.name || 'Your anaesthetist’s practice'}</div>
            <div className="text-xs text-slate-500">Anaesthetic cost estimate</div>
          </div>
          <LockClosedIcon className="h-5 w-5 text-slate-400" title="Secure link" />
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-6">
        {phase === 'loading' && <p className="py-16 text-center text-slate-400">Opening your estimate…</p>}

        {phase === 'refused' && (
          <div className="flex flex-col items-center rounded-xl border border-slate-200 bg-white px-6 py-10 text-center">
            {refused.code === 'answered' ? (
              <>
                {refused.decision === 'accepted' ? <CheckCircleIcon className="h-12 w-12 text-green-600" /> : <XCircleIcon className="h-12 w-12 text-slate-400" />}
                <h1 className="mt-3 text-xl font-bold text-brand-deep">You have already {refused.decision === 'accepted' ? 'accepted' : 'declined'} this estimate</h1>
                <p className="mt-2 max-w-md text-sm text-slate-600">
                  {refused.responded_at ? `On ${fmtWhen(refused.responded_at)}${refused.response_name ? `, by ${refused.response_name}` : ''}. ` : ''}
                  Please contact the practice if you want to change your answer.
                </p>
              </>
            ) : (
              <>
                <ExclamationTriangleIcon className="h-12 w-12 text-amber-500" />
                <h1 className="mt-3 text-xl font-bold text-brand-deep">{refused.network ? 'We couldn’t reach the practice' : REFUSED[refused.code]?.[0] || REFUSED.invalid[0]}</h1>
                <p className="mt-2 max-w-md text-sm text-slate-600">{refused.network ? 'Please check your internet connection and open the link again.' : REFUSED[refused.code]?.[1] || REFUSED.invalid[1]}</p>
              </>
            )}
            {practice?.phone && <p className="mt-4 text-sm text-slate-600">Call {practice.name || 'the practice'} on <a href={`tel:${practice.phone}`} className="font-semibold text-brand-teal">{practice.phone}</a></p>}
          </div>
        )}

        {phase === 'done' && (
          <div className="flex flex-col items-center rounded-xl border border-slate-200 bg-white px-6 py-10 text-center">
            {decision === 'accept' ? <CheckCircleIcon className="h-12 w-12 text-green-600" /> : <XCircleIcon className="h-12 w-12 text-slate-400" />}
            <h1 className="mt-3 text-xl font-bold text-brand-deep">{decision === 'accept' ? 'Thank you — you have accepted the estimate' : 'Thank you for letting us know'}</h1>
            <p className="mt-2 max-w-md text-sm text-slate-600">
              {decision === 'accept'
                ? 'Your acceptance has been recorded and your anaesthetist has been told. You can close this page.'
                : 'Your anaesthetist has been told. The practice will contact you before your procedure.'}
            </p>
          </div>
        )}

        {phase === 'view' && q && (
          <div className="space-y-4">
            <div>
              <h1 className="text-xl font-bold text-brand-deep">Hello {q.first_names},</h1>
              <p className="mt-1 text-sm text-slate-600">
                Here is an estimate of the anaesthetic fees for your procedure, so that you know what to expect before the day. Please read it and let us know whether you accept it.
              </p>
            </div>

            <section className="rounded-xl border border-slate-200 bg-white p-4">
              <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                <div><dt className="text-xs text-slate-500">Date</dt><dd className="font-semibold">{fmtDate(q.service_date)}</dd></div>
                <div><dt className="text-xs text-slate-500">Anaesthetist</dt><dd className="font-semibold">{q.doctor}</dd></div>
                {q.hospital_name && <div><dt className="text-xs text-slate-500">Hospital</dt><dd className="font-semibold">{q.hospital_name}</dd></div>}
                {q.surgeon && <div><dt className="text-xs text-slate-500">Surgeon</dt><dd className="font-semibold">{q.surgeon}</dd></div>}
                {q.procedure_text && <div className="sm:col-span-2"><dt className="text-xs text-slate-500">Procedure</dt><dd className="font-semibold">{q.procedure_text}</dd></div>}
              </dl>
            </section>

            <section className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <div className="divide-y divide-slate-100 text-sm">
                <div className="flex items-center justify-between px-4 py-3">
                  <span>Estimated anaesthetic fee</span>
                  <span className="font-semibold tabular-nums">{rands(q.practice_total)}</span>
                </div>
                {q.payer === 'private' ? (
                  <div className="px-4 py-3 text-slate-600">You are paying the account yourself.</div>
                ) : (
                  <div className="flex items-center justify-between px-4 py-3">
                    <span>{q.scheme_name || 'Your medical aid'} is likely to pay</span>
                    <span className="font-semibold tabular-nums">{q.scheme_total == null ? 'Not known' : rands(q.scheme_total)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between bg-brand-deep px-4 py-4 text-white">
                  <span className="font-semibold">You are likely to pay</span>
                  <span className="text-2xl font-bold tabular-nums">{q.shortfall == null ? '—' : rands(q.shortfall)}</span>
                </div>
              </div>
              {q.shortfall == null && (
                <p className="px-4 py-3 text-sm text-amber-800">We couldn’t estimate what your medical aid will pay — the practice will contact you about it.</p>
              )}
              <button onClick={() => setShowLines(!showLines)} className="flex w-full items-center justify-between border-t border-slate-100 px-4 py-2.5 text-left text-sm font-semibold text-brand-teal">
                How the fee is worked out <ChevronDownIcon className={`h-4 w-4 transition ${showLines ? 'rotate-180' : ''}`} />
              </button>
              {showLines && (
                <ul className="divide-y divide-slate-100 border-t border-slate-100 text-sm">
                  {q.lines.map((l, i) => (
                    <li key={`${l.code}-${i}`} className="flex gap-3 px-4 py-2">
                      <span className="w-12 shrink-0 font-mono text-xs text-slate-500">{l.code}</span>
                      <span className="flex-1 text-slate-700">{l.description}</span>
                      <span className="tabular-nums text-slate-700">{rands(l.amount)}</span>
                    </li>
                  ))}
                  {q.duration_minutes != null && <li className="px-4 py-2 text-xs text-slate-500">Based on an expected anaesthetic time of about {q.duration_minutes} minutes.</li>}
                </ul>
              )}
            </section>

            {q.note && <section className="rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-slate-700"><b>A note from the practice:</b> {q.note}</section>}

            <section className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
              <p className="font-semibold text-slate-700">Please note</p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                <li>This is an estimate. The final account depends on the procedure actually done, how long the anaesthetic takes, and anything that changes on the day.</li>
                <li>What your medical aid pays depends on your plan and benefits, and may be less than shown. Check with them if you are unsure.</li>
                <li>You are responsible for any amount your medical aid does not pay.</li>
              </ul>
            </section>

            {!declining ? (
              <section className="space-y-3 rounded-xl border-2 border-brand-teal/50 bg-white p-4">
                <label className="flex items-start gap-3 text-sm text-slate-700">
                  <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 h-5 w-5 rounded border-slate-300" />
                  <span>{data.consent_text}</span>
                </label>
                <label className="block text-sm font-semibold text-slate-700">Your full name
                  <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" placeholder="As on your ID" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-3 text-base focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30" />
                </label>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <button onClick={() => respond('accept')} disabled={!!saving || !agree || name.trim().length < 3} className="w-full rounded-lg bg-brand-gradient px-4 py-3.5 text-base font-semibold text-white disabled:opacity-50">
                  {saving === 'accept' ? 'Saving…' : 'I accept this estimate'}
                </button>
                <button onClick={() => { setDeclining(true); setError(''); }} className="w-full py-1 text-sm font-semibold text-slate-500 hover:text-slate-700">I don’t accept it</button>
              </section>
            ) : (
              <section className="space-y-3 rounded-xl border border-slate-300 bg-white p-4">
                <p className="text-sm font-semibold text-slate-700">Tell us why, so the practice can help</p>
                <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. The amount I would pay is too high — please call me." className="w-full rounded-lg border border-slate-300 px-3 py-2 text-base focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30" />
                {error && <p className="text-sm text-red-600">{error}</p>}
                <button onClick={() => respond('decline')} disabled={!!saving} className="w-full rounded-lg bg-slate-700 px-4 py-3 text-base font-semibold text-white disabled:opacity-50">{saving === 'decline' ? 'Saving…' : 'Decline the estimate'}</button>
                <button onClick={() => setDeclining(false)} className="w-full py-1 text-sm font-semibold text-slate-500 hover:text-slate-700">Back</button>
              </section>
            )}

            <p className="text-center text-xs text-slate-400">
              {/* The link ends at midnight, so the last day it works is the day before. */}
              This link works once, up to and including {new Date(new Date(q.expires_at).getTime() - 1000).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long' })}.
              {practice?.phone && <> Questions? Call <a href={`tel:${practice.phone}`} className="font-semibold text-brand-teal">{practice.phone}</a>.</>}
            </p>
          </div>
        )}

        <p className="mt-8 flex items-center justify-center gap-1.5 text-xs text-slate-400">
          <Logo variant="icon" className="h-4 w-4" /> Powered by OmniPrac
        </p>
      </main>
    </div>
  );
};

export default PublicQuote;
